import React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import { AppCTX } from '../data/AppContext';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListSubheader from '@mui/material/ListSubheader';
import Switch from '@mui/material/Switch';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import { Fade, Grow } from '@mui/material';

// TODO: These will be replaced with API calls when endpoints are implemented
// const SUPERUSER_UID = process.env.REACT_APP_SUPERUSER;

// REFACTORED: This component now uses teamData from AppContext instead of Firebase
// - Removed Firebase dependencies and data fetching
// - Uses existing teamData that's populated by the new API endpoint
// - Invite generation now uses real API endpoint (/api/invites)
// - Form collects phone and name, memberType is hardcoded to 'salesrep'

// Custom Toggle Component
const CustomToggle = styled(Box)(({ checked }) => ({
  position: 'relative',
  width: 60,
  height: 24,
  backgroundColor: checked ? '#1976d2' : 'rgba(255, 255, 255, 0.2)',
  borderRadius: 12,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  padding: '0 4px',
  '&:hover': {
    opacity: 0.9,
  }
}));

const ToggleThumb = styled(Box)(({ checked }) => ({
  width: 18,
  height: 18,
  backgroundColor: '#e0e0e0',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
  transform: checked ? 'translateX(36px)' : 'translateX(0)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
  flexShrink: 0,
}));

const ToggleText = styled(Typography)(({ checked }) => ({
  position: 'absolute',
  left: '8px',
  color: '#fff',
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  opacity: checked ? 1 : 0,
  transition: 'opacity 0.3s ease',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '32px',
}));

// Enhanced styled components matching ClientDetails
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
  borderRadius: 16,
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #222 0%, #2f2f2f 100%)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused': {
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      border: '2px solid #1976d2',
      boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.1)',
    }
  },
  '& .MuiInputLabel-root': {
    color: '#bbb',
    fontWeight: 500,
  },
  '& .MuiInputBase-input': {
    color: '#fff',
    fontWeight: 500,
    fontSize: 16,
  }
}));

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// Predefined avatar colors for consistent appearance
const AVATAR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', 
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
  '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2'
];

function getAvatarColor(name, memberColor) {
  // Use the member's actual color if available, otherwise generate from name
  if (memberColor && memberColor.trim() !== '') {
    return memberColor;
  }
  // Fallback to generated color based on name
  const index = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

function TeamMembersListUI({ members, isSuperuser, onAreaManagerToggle }) {
  return (
    <StyledCard sx={{ 
      width: '100%', 
      maxWidth: 480, 
      height: '100%', // 100% of parent container instead of viewport height
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Enhanced Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #252525 0%, #2f2f2f 100%)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 20,
          letterSpacing: 0.5,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          p: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%'
        }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            boxShadow: '0 0 12px rgba(25, 118, 210, 0.5)',
            flexShrink: 0
          }} />
          <Typography sx={{ flex: 1, fontWeight: 700 }}>
            Team Members
          </Typography>
          <Box sx={{ 
            background: 'rgba(255, 255, 255, 0.1)', 
            px: 2, 
            py: 0.5, 
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            flexShrink: 0
          }}>
            {members.length}
          </Box>
        </Box>
        
        {/* Enhanced Scrollable List Container */}
        <Box sx={{ 
          flex: 1,
          overflowY: 'auto',
          width: '100%',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '4px',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
            },
          },
        }}>
          <List sx={{ p: 0, width: '100%' }}>
            {members.map((member, index) => (
              <Grow in={true} timeout={300 + index * 50} key={member.name + member.color}>
                <ListItem 
                  sx={{ 
                    px: 3, 
                    py: 2,
                    width: '100%',
                    borderBottom: index < members.length - 1 ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 56, flexShrink: 0 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getAvatarColor(member.name, member.color), 
                        color: '#fff', 
                        fontWeight: 700,
                        width: 48,
                        height: 48,
                        fontSize: 18,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Typography 
                        sx={{ 
                          color: '#fff', 
                          fontWeight: 600, 
                          fontSize: 16,
                          lineHeight: 1.2
                        }}
                      >
                        {member.name}
                      </Typography>
                    }
                    secondary={
                      <Typography 
                        sx={{ 
                          color: '#999', 
                          fontSize: 14,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          mt: 0.5
                        }}
                      >
                        {member.memberType}
                      </Typography>
                    }
                    sx={{ 
                      mr: 2, 
                      flex: 1,
                      minWidth: 0 // Allow text to shrink
                    }}
                  />
                  
                  {isSuperuser && (
                    <Box sx={{ flexShrink: 0 }}>
                      <CustomToggle 
                        checked={member.areaManager === true}
                        onClick={() => onAreaManagerToggle(member.name, member.areaManager !== true)}
                      >
                        <ToggleThumb checked={member.areaManager === true} />
                        <ToggleText checked={member.areaManager === true}>Area Manager</ToggleText>
                      </CustomToggle>
                    </Box>
                  )}
                </ListItem>
              </Grow>
            ))}
          </List>
        </Box>
      </CardContent>
    </StyledCard>
  );
}

export default function TeamManager({ onClose }) {
  const { user, teamData, setTeamData, useApiClient, updateAreaManager, setStatus } = React.useContext(AppCTX);
  const apiClient = useApiClient();
  const [inviteCode, setInviteCode] = React.useState(null);
  const [phone, setPhone] = React.useState('+1');
  const [name, setName] = React.useState('');
  const [codeError, setCodeError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // teamData is now automatically populated from AppContext via the new API
  // No need to fetch separately - it's already available

  // Filter members based on search query
  const filteredMembers = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return teamData?.teamMembers || [];
    }
    return (teamData?.teamMembers || []).filter(member =>
      member.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [teamData?.teamMembers, searchQuery]);

  async function handleAreaManagerToggle(memberName, newValue) {
    if (!teamData) return;
    try {
      await updateAreaManager(memberName, newValue);
    } catch (err) {
      setStatus({ notificationType: 'error', message: err.message || 'Failed to update area manager' });
    }
  }

  // Permission check for area manager functionality
  const isSuperuser = user?.data?.memberType === 'admin' || user?.data?.memberType === 'superuser';

  async function handleGenerateInviteCode() {
    setCodeError('');
    setLoading(true);
    
    try {
      // Validate required fields
      if (!phone.trim() || !name.trim()) {
        setCodeError('Phone number and name are required.');
        setLoading(false);
        return;
      }
      
      // Validate phone number format (basic international format check)
      if (!phone.startsWith('+') || phone.length < 10) {
        setCodeError('Please enter a valid phone number in international format (e.g., +1234567890).');
        setLoading(false);
        return;
      }
      
      const response = await apiClient.post('/api/invites', {
        phone: phone.trim(),
        name: name.trim(),
        memberType: 'salesrep', // Hardcoded as requested
        teamId: teamData?.teamId || user?.data?.teamId
      });
      
      if (response.success) {
        setInviteCode(response.inviteCode);
        // Clear form after successful generation
        setPhone('+1');
        setName('');
      } else {
        setCodeError(response.error || 'Failed to generate invite code.');
      }
    } catch (err) {
      setCodeError(err.message || 'Failed to generate invite code. Please try again.');
    }
    
    setLoading(false);
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100vw', 
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      color: '#fff', 
      p: 0, 
      m: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <AppBar sx={{ 
        position: 'relative', 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ 
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { 
                background: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ 
            ml: 2, 
            flex: 1, 
            color: '#fff', 
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '0.5px'
          }} variant="h6">
            Team Management
          </Typography>
        </Toolbar>
      </AppBar>
      
      {/* Main Content Area - Fixed Layout */}
      <Box sx={{ 
        flex: 1, // Takes remaining space after header
        p: 4, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        gap: 4,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
      }}>
        {/* Team Members Section - 50% of parent */}
        <Box sx={{ 
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          height: '50%', // 50% of parent container
          flexShrink: 0 // Prevent shrinking
        }}>
          {/* Enhanced Search Bar */}
          <Grow in={true} timeout={300}>
            <StyledTextField
              placeholder="Search team members..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ 
                width: '100%', 
                maxWidth: 480,
                '& .MuiInputBase-input': {
                  '&::placeholder': {
                    color: '#999',
                    opacity: 1,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ 
                    color: '#999', 
                    mr: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    🔍
                  </Box>
                ),
              }}
            />
          </Grow>
          
          <TeamMembersListUI members={filteredMembers} isSuperuser={isSuperuser} onAreaManagerToggle={handleAreaManagerToggle} />
        </Box>

        {/* Enhanced Add New Member Section - Content-based height */}
        {user?.data?.memberType === 'admin' && (
          <Grow in={true} timeout={500}>
            <StyledCard sx={{ 
              width: '100%', 
              maxWidth: 480
              // Remove alignSelf to center the card properly
            }}>
              <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', width: '100%' }}>
                {/* Enhanced Header */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2, // Reduced margin
                  gap: 2,
                  width: '100%',
                  flexShrink: 0
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                    boxShadow: '0 0 12px rgba(76, 175, 80, 0.5)',
                    flexShrink: 0
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#fff', 
                      fontWeight: 700,
                      fontSize: 18,
                      flex: 1
                    }}
                  >
                    Invite New Member
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%'
                }}>
                  {/* Show input fields only when no invite code is generated */}
                  {!inviteCode && (
                    <>
                      <StyledTextField
                        label="Phone Number"
                        value={phone}
                        onChange={e => {
                          let value = e.target.value;
                          // Ensure the input always starts with +
                          if (!value.startsWith('+')) {
                            value = '+' + value;
                          }
                          // Remove any non-digit characters except the leading +
                          value = value.replace(/[^\d+]/g, '');
                          // Limit to reasonable length (country code + area code + number)
                          if (value.length <= 15) {
                            setPhone(value);
                          }
                        }}
                        size="small"
                        placeholder="+1234567890"
                        sx={{ 
                          width: '100%',
                          '& .MuiInputBase-input': {
                            color: '#fff',
                            fontWeight: 500
                          }
                        }}
                        InputLabelProps={{ style: { color: '#bbb' } }}
                      />
                      
                      <StyledTextField
                        label="Full Name"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        size="small"
                        placeholder="John Doe"
                        sx={{ 
                          width: '100%',
                          '& .MuiInputBase-input': {
                            color: '#fff',
                            fontWeight: 500
                          }
                        }}
                        InputLabelProps={{ style: { color: '#bbb' } }}
                      />

                      <Button
                        variant="contained"
                        onClick={handleGenerateInviteCode}
                        disabled={loading || !phone.trim() || !name.trim()}
                        sx={{ 
                          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                          color: '#fff', 
                          fontWeight: 700,
                          fontSize: 14,
                          py: 1.5,
                          px: 3,
                          borderRadius: 3,
                          textTransform: 'none',
                          boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          width: '100%',
                          '&:hover': { 
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(25, 118, 210, 0.4)'
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #555 0%, #444 100%)',
                            color: '#999',
                            transform: 'none',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        {loading ? 'Generating...' : 'GENERATE INVITE CODE'}
                      </Button>
                    </>
                  )}

                  {/* Show error message */}
                  {codeError && (
                    <Fade in={true} timeout={300}>
                      <Typography sx={{ 
                        color: '#f44336', 
                        textAlign: 'center',
                        fontWeight: 600,
                        p: 1.5,
                        background: 'rgba(244, 67, 54, 0.1)',
                        borderRadius: 2,
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        width: '100%',
                        fontSize: 13
                      }}>
                        {codeError}
                      </Typography>
                    </Fade>
                  )}

                  {/* Show invite code and restart button */}
                  {inviteCode && (
                    <Fade in={true} timeout={500}>
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%'
                      }}>
                        <Box sx={{ 
                          p: 2,
                          background: 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)',
                          borderRadius: 3, 
                          border: '2px solid #1976d2',
                          textAlign: 'center',
                          boxShadow: '0 8px 32px rgba(25, 118, 210, 0.2)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                          }
                        }}>
                          <Typography 
                            sx={{ 
                              color: '#fff', 
                              fontSize: 24,
                              fontWeight: 700, 
                              letterSpacing: 2,
                              mb: 1,
                              textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                            }}
                          >
                            {inviteCode}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#bbb', 
                              fontSize: 12,
                              lineHeight: 1.3,
                              fontWeight: 500
                            }}
                          >
                            Share this code with the new team member. It can only be used once.
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          onClick={() => {
                            setInviteCode(null);
                            setCodeError('');
                            setPhone('+1');
                            setName('');
                          }}
                          sx={{ 
                            border: '2px solid #1976d2',
                            color: '#1976d2', 
                            fontWeight: 700,
                            fontSize: 14,
                            py: 1.5,
                            px: 3,
                            borderRadius: 3,
                            textTransform: 'none',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            width: '100%',
                            '&:hover': { 
                              background: 'rgba(25, 118, 210, 0.1)',
                              border: '2px solid #42a5f5',
                              color: '#42a5f5',
                              transform: 'translateY(-1px)',
                            }
                          }}
                        >
                          GENERATE NEW CODE
                        </Button>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </CardContent>
            </StyledCard>
          </Grow>
        )}
      </Box>
    </Box>
  );
} 